import type { BlockNoteEditor, PartialBlock } from "@blocknote/core";
import { prepareImportedHtml } from "./briefImportHtml";
import { extractPdfHtml } from "./briefImportPdf";
import { isDocumentFile } from "./editorFileTypes";
import {
  hasLocalMediaInEditor,
  processAndUploadMediaBlocks,
} from "./editorMedia";

const applyImportedBlocks = (
  editor: BlockNoteEditor,
  blocks: PartialBlock[]
) => {
  editor.replaceBlocks(
    editor.document,
    blocks.length ? blocks : [{ type: "paragraph", content: "" }]
  );
};

const uploadImportedImages = async (editor: BlockNoteEditor) => {
  if (!hasLocalMediaInEditor(editor)) return;
  await processAndUploadMediaBlocks(editor);
};

const importHtmlIntoEditor = async (editor: BlockNoteEditor, rawHtml: string) => {
  const html = prepareImportedHtml(rawHtml);
  if (!html.trim()) {
    throw new Error("No readable content found in this document.");
  }
  const blocks = editor.tryParseHTMLToBlocks(html) as PartialBlock[];
  applyImportedBlocks(editor, blocks);
  await uploadImportedImages(editor);
};

const extractDocxHtml = async (file: File): Promise<string> => {
  const mammoth = await import("mammoth");
  const result = await mammoth.convertToHtml(
    { arrayBuffer: await file.arrayBuffer() },
    {
      convertImage: mammoth.images.imgElement(async (image) => {
        if (!image.contentType.startsWith("image/")) {
          return { src: "" };
        }
        const data = await image.read("base64");
        return { src: `data:${image.contentType};base64,${data}` };
      }),
    }
  );
  return result.value || "";
};

const isPdfFile = (file: File) =>
  file.name.toLowerCase().endsWith(".pdf") || file.type === "application/pdf";

const isDocxFile = (file: File) =>
  file.name.toLowerCase().endsWith(".docx") ||
  file.type ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const isDocFile = (file: File) =>
  file.name.toLowerCase().endsWith(".doc") ||
  file.type === "application/msword";

/** Import DOC/DOCX/PDF into the editor. Images go to Firebase; video is discarded. */
export const importBriefFromFile = async (
  editor: BlockNoteEditor,
  file: File
): Promise<void> => {
  if (!isDocumentFile(file)) {
    throw new Error("Unsupported file. Upload a .docx or .pdf file.");
  }

  if (isPdfFile(file)) {
    await importHtmlIntoEditor(editor, await extractPdfHtml(file));
    return;
  }

  if (isDocxFile(file) || isDocFile(file)) {
    try {
      await importHtmlIntoEditor(editor, await extractDocxHtml(file));
      return;
    } catch (error) {
      if (isDocFile(file) && !isDocxFile(file)) {
        throw new Error(
          "Please upload a .docx file. Older .doc format is not supported."
        );
      }
      throw error;
    }
  }
};
